import React, { useState, useEffect } from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

function Alert({
  variant = "success",
  message,
  dismissible = true,
  timeout = 3000,
  onClose,
  position = "right",
  type,
}) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    let timer;
    if (type !== "button" && timeout) {
      timer = setTimeout(() => {
        setShow(false);
        if (onClose) onClose();
      }, timeout);
    }
    return () => clearTimeout(timer);
  }, [timeout, onClose, type]);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setShow(false);
    if (onClose) onClose();
  };

  if (!show) return null;

  const isDanger = variant === "danger" || variant === "error";
  const isWarning = variant === "warning";
  const isInfo = variant === "info";

  // Brand Palette for Toast Variants
  const colors = isDanger
    ? {
        bg: "#fef2f2",
        border: "#fecdd3",
        text: "#991b1b",
        icon: "#e63946",
        bar: "#e63946"
      }
    : isWarning
    ? {
        bg: "#fffbeb",
        border: "#fde68a",
        text: "#92400e",
        icon: "#f59e0b",
        bar: "#f59e0b"
      }
    : isInfo
    ? {
        bg: "#eff6ff",
        border: "#bfdbfe",
        text: "#1e40af",
        icon: "#3b82f6",
        bar: "#3b82f6"
      }
    : {
        bg: "#f0fdf4",
        border: "#bbf7d0",
        text: "#166534",
        icon: "#10b981",
        bar: "#10b981"
      };

  const IconComponent = isDanger
    ? AlertCircle
    : isWarning
    ? AlertTriangle
    : isInfo
    ? Info
    : CheckCircle2;

  const getPositionStyles = () => {
    return { top: "24px", right: "24px", left: "auto", transform: "none" };
  };

  const keyFrames = `
    @keyframes alertProgressBar {
      0% { width: 100%; }
      100% { width: 0%; }
    }
    @keyframes alertFadeIn {
      from { opacity: 0; transform: translateY(-8px) scale(0.96); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  `;

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 10000,
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
        maxWidth: "420px",
        minWidth: "280px",
        cursor: "pointer",
        overflow: "hidden",
        animation: "alertFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        ...getPositionStyles(),
      }}
      onClick={handleClose}
      role="alert"
    >
      <style>{keyFrames}</style>

      {/* Icon */}
      <IconComponent size={20} style={{ color: colors.icon, flexShrink: 0 }} />

      {/* Message Content */}
      <div
        style={{
          flex: 1,
          fontSize: "13.5px",
          fontWeight: 600,
          color: colors.text,
          lineHeight: "1.4",
          textAlign: "left",
          wordBreak: "break-word"
        }}
      >
        {message}
      </div>

      {/* Dismiss button */}
      {dismissible && (
        <button
          type="button"
          onClick={handleClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "2px",
            color: colors.text,
            opacity: 0.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
            transition: "opacity 0.15s ease"
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
        >
          <X size={16} />
        </button>
      )}

      {/* Progress Bar */}
      {timeout > 0 && type !== "button" && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: "3px",
            backgroundColor: colors.bar,
            animation: `alertProgressBar ${timeout / 1000}s linear forwards`,
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px"
          }}
        />
      )}
    </div>
  );
}

export default Alert;
